import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, Plus, Loader2, Link2, Unlink } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { useMeetingDialogs } from "./MeetingsPanel";
import { saHoliday } from "./saHolidays";
import { ymd, MEET_COLORS, fmtTime } from "./bizUtils";

const DAYS = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

const GoogleCard = () => {
  const [s, setS] = useState(null);
  const load = () => api.gcalStatus().then(setS).catch(() => setS(null));
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search).get("gcal");
    if (p) {
      const why = new URLSearchParams(window.location.search).get("reason");
      const msg = p === "ok" ? "تم ربط Google Calendar" : p === "denied" ? "لم يكتمل الربط: تم رفض الإذن من حساب Google"
        : why === "invalid_client" ? "لم يكتمل الربط: الرمز السري (Client Secret) غير صحيح — حدّثه من الإعدادات"
        : `لم يكتمل ربط Google Calendar${why ? ` (${why})` : ""}`;
      toast[p === "ok" ? "success" : "error"](msg, { duration: 9000 });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);
  if (!s) return null;
  const connect = async () => { try { window.location.href = (await api.gcalConnect()).url; } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الربط"); } };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200/70 bg-white px-4 py-3" data-testid="gcal-card">
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-blue-50 font-heading text-sm font-extrabold text-blue-600">G</span>
      <div className="me-auto text-sm"><p className="font-bold text-slate-800">Google Calendar</p>
        <p className="text-xs text-slate-500" data-testid="gcal-state">{!s.configured ? "لم يُفعَّل الربط بعد من إعدادات المنصة" : s.connected ? `مربوط بـ ${s.email} — المواعيد تُزامَن في الاتجاهين` : "اربط حسابك لمزامنة المواعيد في الاتجاهين"}</p></div>
      {s.configured && (s.connected
        ? <Button size="sm" variant="outline" onClick={async () => { await api.gcalDisconnect(); load(); }} className="gap-1" data-testid="gcal-disconnect"><Unlink className="h-4 w-4" /> فصل</Button>
        : <Button size="sm" onClick={connect} className="gap-1" data-testid="gcal-connect"><Link2 className="h-4 w-4" /> ربط</Button>)}
    </div>
  );
};

export const BusinessCalendar = ({ employees = [] }) => {
  const [cur, setCur] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState(null);
  const grid = useMemo(() => { const s = new Date(cur); s.setDate(1 - cur.getDay()); return Array.from({ length: 42 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; }); }, [cur]);
  const load = useCallback(() => Promise.all([
    api.meetings(ymd(grid[0]), ymd(grid[41])),
    api.reportCalendar(ymd(grid[0]), ymd(grid[41])).catch(() => ({ items: [] })),
  ]).then(([m, r]) => setData({ ...m, reports: (r.items || []).map((x) => ({ ...x, source: "report", start: `${x.date}T23:59` })) }))
    .catch(() => setData({ items: [], google: [], reports: [] })), [grid]);
  useEffect(() => { setData(null); load(); }, [load]);
  const { setForm, setRoom, ui } = useMeetingDialogs(load);
  const byDay = useMemo(() => {
    const m = {};
    [...(data?.items || []).filter((x) => x.status !== "cancelled"), ...(data?.google || []), ...(data?.reports || [])].forEach((e) => { const k = (e.start || "").slice(0, 10); (m[k] = m[k] || []).push(e); });
    Object.values(m).forEach((l) => l.sort((a, b) => a.start.localeCompare(b.start)));
    return m;
  }, [data]);
  const today = ymd(new Date());
  const title = cur.toLocaleDateString("ar-SA-u-nu-latn-ca-gregory", { month: "long", year: "numeric" });
  const hFmt = (d, o) => new Intl.DateTimeFormat("ar-SA-u-ca-islamic-umalqura-nu-latn", o).format(d);
  const hDay = (d) => Number(hFmt(d, { day: "numeric" }).replace(/\D/g, ""));
  const lastDay = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
  const hA = hFmt(cur, { month: "long", year: "numeric" }), hB = hFmt(lastDay, { month: "long", year: "numeric" });
  const hTitle = hA === hB ? hA : `${hFmt(cur, { month: "long" })} – ${hB}`;
  const shift = (n) => setCur((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));
  return (
    <div className="space-y-5" data-testid="business-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronRight className="h-4 w-4" /></Button>
          <div className="min-w-40 text-center">
            <h2 className="font-heading text-2xl font-extrabold" data-testid="cal-title">{title}</h2>
            <p className="text-xs font-semibold text-emerald-700" data-testid="cal-hijri-title">{hTitle}</p>
          </div>
          <Button size="icon" variant="outline" onClick={() => shift(1)} data-testid="cal-next"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => { const d = new Date(); setCur(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="cal-today">اليوم</Button>
          {!data && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
        <Button onClick={() => setForm({ date: today })} className="gap-1.5 rounded-xl" data-testid="cal-new"><Plus className="h-4 w-4" /> موعد جديد</Button>
      </div>
      <GoogleCard />
      <div className="overflow-x-auto rounded-2xl border border-slate-200/70 bg-white">
        <div className="grid min-w-[720px] grid-cols-7">
          {DAYS.map((d) => <div key={d} className="border-b border-slate-100 py-2 text-center text-xs font-bold text-slate-500">{d}</div>)}
          {grid.map((d) => {
            const k = ymd(d); const ev = byDay[k] || []; const out = d.getMonth() !== cur.getMonth(); const hol = saHoliday(d);
            return (
              <div key={k} onDoubleClick={() => setForm({ date: k })} className={`group min-h-28 border-b border-l border-slate-100 p-1.5 ${out ? "bg-slate-50/60" : hol?.off ? "bg-emerald-50/70" : hol?.soft ? "bg-amber-50/40" : ""}`} data-testid={`cal-day-${k}`}>
                <div className="mb-1 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className={`grid h-6 w-6 place-items-center rounded-full text-xs ${k === today ? "bg-blue-600 font-bold text-white" : out ? "text-slate-300" : "text-slate-600"}`}>{d.getDate()}</span>
                    <span className={`text-[10px] font-semibold ${out ? "text-emerald-300" : "text-emerald-700"}`} data-testid={`cal-hijri-${k}`}>
                      {hDay(d) === 1 ? hFmt(d, { day: "numeric", month: "short" }) : hDay(d)}</span>
                  </div>
                  {hol && !hol.soft && <span className={`truncate rounded-full px-1.5 py-0.5 text-[10px] font-bold ${hol.off ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-800"}`} title={hol.off ? "إجازة رسمية" : "مناسبة"} data-testid={`cal-holiday-${k}`}>{hol.label}</span>}
                  <button onClick={() => setForm({ date: k })} className="opacity-60 transition-opacity sm:opacity-0 sm:group-hover:opacity-100" data-testid={`cal-add-${k}`}><Plus className="h-3.5 w-3.5 text-slate-400" /></button>
                </div>
                <div className="space-y-0.5">{ev.slice(0, 3).map((e) => e.source === "report" ? (
                  <button key={e.id} onClick={() => { sessionStorage.setItem("corr_tab_once", "reports"); window.dispatchEvent(new CustomEvent("wz-goto", { detail: "correspondence" })); }} title={`${e.title} — ${e.department}`} data-testid={`cal-rep-${e.id}`}
                    className={`block w-full truncate rounded px-1.5 py-0.5 text-right text-[10px] font-semibold ${e.submitted ? "bg-emerald-100 text-emerald-800 line-through" : "bg-indigo-100 text-indigo-800"}`}>تقرير · {e.title}</button>
                ) : e.source === "google" ? (
                  <a key={e.id} href={e.link} target="_blank" rel="noreferrer" className="block truncate rounded border border-dashed border-blue-300 px-1.5 py-0.5 text-[10px] text-blue-700" title={e.title} data-testid={`cal-g-${e.google_id}`}>G · {e.all_day ? "" : fmtTime(e.start)} {e.title}</a>
                ) : (
                  <button key={e.id} onClick={() => setRoom(e.id)} title={e.title} data-testid={`cal-ev-${e.id}`}
                    className={`block w-full truncate rounded px-1.5 py-0.5 text-right text-[10px] font-medium text-white ${MEET_COLORS[e.color] || MEET_COLORS.blue}`}>{fmtTime(e.start)} {e.title}</button>))}
                  {ev.length > 3 && <p className="px-1 text-[10px] text-slate-400">+{ev.length - 3} أخرى</p>}</div>
              </div>);
          })}
        </div>
      </div>
      {ui(employees)}
    </div>
  );
};
